import React, { useEffect, useState } from "react";
import { Prompt } from "react-router";
import CalibrationApi from "../../../../api/CalibrationApi";
import ImagerApi from "../../../../api/ImagerApi";
import { ELightPath } from "../../../../Model/CaptureProfile";
import { EModuleType } from "../../../../Model/EModuleType";
import { EOpticFeature } from "../../../../Model/EOpticFeature";
import {
  FilterCubeCalibration,
  ImagerCalibration,
  ObjectiveCalibration,
} from "../../../../Model/ImagerCalibration";
import { ImagerCapability } from "../../../../Model/ImagerCapabilities";
import { ESpeed } from "../../../../Model/ModuleState";
import { getImagerCapabilities } from "../../../../redux/reducers/ImagerCapabilitiesReducer";
import { getModules } from "../../../../redux/reducers/ModuleReducer";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import ImagerUtil, {
  OpticMotorCalibration,
} from "../../../../Utility/ImagerUtil";
import LightPathUtil from "../../../../Utility/LightPathUtil";
import Wait from "../../../../Wait";
import CommandPanel from "../../../CommandPanel/CommandPanel";
import CommandPanelComboBox from "../../../CommandPanel/CommandPanelComboBox";
import ImagerManualControl from "../../../Imager/ImagerManualControl";
import MotorOffsetTable from "./MotorOffsetTable";
import OpticMotorControl from "./OpticMotorControl";
import Disabled from "../../../Controls/Disabled";

function getMotorDisplayValues(
  imagerCapabilities: ImagerCapability,
  lightPath: ELightPath
) {
  const lightpathCapabilities =
    imagerCapabilities.lightpathCollection.byId[lightPath];
  const filterCube = lightpathCapabilities.optic.filterCubes;
  const objectiveZoom = lightpathCapabilities.optic.objectiveZoom;
  let result: { display: string; value: EOpticFeature }[] = [];
  if (objectiveZoom) {
    result.push({
      display: "Objective Zoom",
      value: EOpticFeature.ObjectiveZoom,
    });
  }

  if (filterCube) {
    result.push({
      display: "Filter Cubes",
      value: EOpticFeature.FilterCubes,
    });
  }
  return result;
}

type Props = {
  imagerIdentifier: string;
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  imagerCalibration: ImagerCalibration | undefined;
  setImagerCalibration: (imagerCalibration: ImagerCalibration) => void;
  isCalibrationUpdated: boolean;
  setIsCalibrationUpdated: (isCalibrationUpdated: boolean) => void;
};

export default function MotorCalibrationCommandPanel({
  imagerIdentifier,
  setOnApply,
  setOnCancel,
  imagerCalibration,
  setImagerCalibration,
  isCalibrationUpdated,
  setIsCalibrationUpdated,
}: Props) {
  const imagerCapabilityCollection = useTypedSelector((state) =>
    getImagerCapabilities(state)
  );
  const lightPath = ImagerUtil.getLightPathWithOpticMotor(
    imagerCapabilityCollection.byId[imagerIdentifier]
  );
  const motorDisplayValues = getMotorDisplayValues(
    imagerCapabilityCollection.byId[imagerIdentifier],
    lightPath
  );
  const [opticFeature, setOpticFeature] = useState(motorDisplayValues[0].value);
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );

  const [speed, setSpeed] = useState(2);

  const [opticMotorCalibration, setOpticMotorCalibration] = useState(
    null as OpticMotorCalibration | null
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    CalibrationApi.getImagerCalibration(imagerIdentifier).then(
      (imagerCalibration) => {
        setImagerCalibration(imagerCalibration);
        setOpticMotorCalibration(
          ImagerUtil.getOpticMotorCalibration(imagerCalibration, lightPath)
        );
      }
    );
  }, [imagerIdentifier]);

  const onCancel = () => {
    setOpticMotorCalibration(
      ImagerUtil.getOpticMotorCalibration(imagerCalibration, lightPath)
    );
    setIsCalibrationUpdated(false);
  };

  const onApply = () => {
    if (opticMotorCalibration != null) {
      let imagerCalibration: ImagerCalibration = {
        identifier: imagerIdentifier,
      };

      if (lightPath === ELightPath.Visible) {
        imagerCalibration.visible = {};
        if (opticMotorCalibration.objectiveZoomCalibration != null) {
          imagerCalibration.visible.objectiveZoom =
            opticMotorCalibration.objectiveZoomCalibration;
        }
      }

      if (lightPath === ELightPath.Ultraviolet) {
        imagerCalibration.ultraviolet = {};
        if (opticMotorCalibration.objectiveZoomCalibration != null) {
          imagerCalibration.ultraviolet.objectiveZoom =
            opticMotorCalibration.objectiveZoomCalibration;
        }
      }

      if (lightPath === ELightPath.Fluorescence) {
        imagerCalibration.fluorescence = {};
        if (opticMotorCalibration.objectiveZoomCalibration != null) {
          imagerCalibration.fluorescence.objectiveZoom =
            opticMotorCalibration.objectiveZoomCalibration;
        }

        if (opticMotorCalibration.filterCubesCalibration != null) {
          imagerCalibration.fluorescence.filterCubes =
            opticMotorCalibration.filterCubesCalibration;
        }
      }

      CalibrationApi.updateImagerCalibration(
        imagerCalibration,
        imagerIdentifier
      ).then(() => setIsCalibrationUpdated(false));
    }
  };

  useEffect(() => {
    setOnApply({ execute: onApply, disabled: !isCalibrationUpdated });
    setOnCancel({ execute: onCancel, disabled: !isCalibrationUpdated });
  }, [isCalibrationUpdated]);

  const imagerState = moduleState.imager;
  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  if (imagerCalibration == null || opticMotorCalibration == null) {
    return <Wait message="Loading imager calibration" />;
  }

  const isLightPathHasOpticMotor = ImagerUtil.getActiveLightPathHasOpticMotor(
    imagerCapabilityCollection.byId[imagerIdentifier],
    imagerState.activeLightPath
  );

  const showCalibration =
    opticMotorCalibration.filterCubesCalibration != null ||
    opticMotorCalibration.objectiveZoomCalibration != null;

  // const position =
  //   opticFeature === EOpticFeature.FilterCubes
  //     ? opticMotorCalibration.filterCubesCalibration?.filterCubes[selectedIndex].position ?? -1
  //     : opticMotorCalibration.objectiveZoomCalibration?.objectives[selectedIndex].position ?? -1;

  const position =
    opticFeature === EOpticFeature.FilterCubes
      ? imagerState.opticMotorPosition.fluorescence.filterCubes ?? -1
      : lightPath === ELightPath.Visible
      ? imagerState.opticMotorPosition.visible.objectiveZoom ?? -1
      : lightPath === ELightPath.Fluorescence
      ? imagerState.opticMotorPosition.fluorescence.objectiveZoom ?? -1
      : imagerState.opticMotorPosition.ultraviolet.objectiveZoom ?? -1;

  const maxPosition =
    opticFeature === EOpticFeature.FilterCubes
      ? opticMotorCalibration.filterCubesCalibration?.maxPosition ?? -1
      : opticMotorCalibration.objectiveZoomCalibration?.maxPosition ?? -1;

  const minPosition =
    opticFeature === EOpticFeature.FilterCubes
      ? opticMotorCalibration.filterCubesCalibration?.minPosition ?? -1
      : opticMotorCalibration.objectiveZoomCalibration?.minPosition ?? -1;

  const onMotorCalibrationUpdated = (
    motorCalibration: Array<FilterCubeCalibration | ObjectiveCalibration>
  ) => {
    if (opticFeature === EOpticFeature.FilterCubes) {
      if (opticMotorCalibration.filterCubesCalibration == null) {
        throw new Error("Filter cube calibration is null");
      }
      setOpticMotorCalibration({
        ...opticMotorCalibration,
        filterCubesCalibration: {
          ...opticMotorCalibration.filterCubesCalibration,
          filterCubes: motorCalibration as FilterCubeCalibration[],
        },
      });
    } else if (opticFeature === EOpticFeature.ObjectiveZoom) {
      if (opticMotorCalibration.objectiveZoomCalibration == null) {
        throw new Error("Objective zoom calibration is null");
      }
      setOpticMotorCalibration({
        ...opticMotorCalibration,
        objectiveZoomCalibration: {
          ...opticMotorCalibration.objectiveZoomCalibration,
          objectives: motorCalibration as ObjectiveCalibration[],
        },
      });
    }
    setIsCalibrationUpdated(true);
  };

  const onMoveToPosition = (position: number) => {
    ImagerApi.moveOpticMotor(
      imagerIdentifier,
      lightPath,
      opticFeature,
      position,
      ESpeed.Fast
    );
  };

  const motorCalibration =
    opticFeature === EOpticFeature.FilterCubes
      ? opticMotorCalibration.filterCubesCalibration?.filterCubes
      : opticMotorCalibration.objectiveZoomCalibration?.objectives;

  if (motorCalibration == null) {
    throw new Error("Motor calibration is null");
  }

  const onInputPosition = () => {
    if (opticFeature === EOpticFeature.FilterCubes) {
      if (opticMotorCalibration.filterCubesCalibration == null) {
        throw new Error("Filter cube calibration is null");
      }
      const newFilterCubesCalibration = [
        ...opticMotorCalibration.filterCubesCalibration.filterCubes,
      ];
      newFilterCubesCalibration[selectedIndex] = {
        ...newFilterCubesCalibration[selectedIndex],
        position: position,
      };
      setOpticMotorCalibration({
        ...opticMotorCalibration,
        filterCubesCalibration: {
          ...opticMotorCalibration.filterCubesCalibration,
          filterCubes: newFilterCubesCalibration,
        },
      });
    } else if (opticFeature === EOpticFeature.ObjectiveZoom) {
      if (opticMotorCalibration.objectiveZoomCalibration == null) {
        throw new Error("Objective zoom calibration is null");
      }
      const newObjectivesCalibration = [
        ...opticMotorCalibration.objectiveZoomCalibration.objectives,
      ];
      newObjectivesCalibration[selectedIndex] = {
        ...newObjectivesCalibration[selectedIndex],
        position: position,
      };
      setOpticMotorCalibration({
        ...opticMotorCalibration,
        objectiveZoomCalibration: {
          ...opticMotorCalibration.objectiveZoomCalibration,
          objectives: newObjectivesCalibration,
        },
      });
    }
    setIsCalibrationUpdated(true);
  };

  const onHome = () => {
    ImagerApi.homeOpticMotor(imagerIdentifier, lightPath, opticFeature);
  };

  let stopMoveOnMouseUp = true;

  const onMoveRight = () => {
    stopMoveOnMouseUp = true;
    ImagerApi.moveOpticMotor(
      imagerIdentifier,
      lightPath,
      opticFeature,
      maxPosition,
      speed
    );
  };

  const onMoveLeft = () => {
    stopMoveOnMouseUp = true;
    ImagerApi.moveOpticMotor(
      imagerIdentifier,
      lightPath,
      opticFeature,
      minPosition,
      speed
    );
  };

  const onMoveMax = () => {
    stopMoveOnMouseUp = false;
    ImagerApi.moveOpticMotor(
      imagerIdentifier,
      lightPath,
      opticFeature,
      maxPosition,
      ESpeed.Fast
    );
  };

  const onMoveMin = () => {
    stopMoveOnMouseUp = false;
    ImagerApi.moveOpticMotor(
      imagerIdentifier,
      lightPath,
      opticFeature,
      minPosition,
      ESpeed.Fast
    );
  };

  const onMouseUp = () => {
    if (stopMoveOnMouseUp)
      ImagerApi.stopOpticMotor(imagerIdentifier, lightPath, opticFeature);
  };

  return (
    <CommandPanel
      style={{
        overflow: "auto",
        WebkitBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        MozBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      }}
    >
      {showCalibration && (
        <>
          <Disabled disabled={!isLightPathHasOpticMotor}>
            <CommandPanelComboBox<number>
              value={opticFeature}
              label="Motor"
              onChange={setOpticFeature}
              displayValues={motorDisplayValues}
            />
            <OpticMotorControl
              position={position}
              onMoveLeft={onMoveLeft}
              onMoveRight={onMoveRight}
              onMoveMin={onMoveMin}
              onMoveMax={onMoveMax}
              onMouseUp={onMouseUp}
              onInputPosition={onInputPosition}
              speed={speed}
              onSpeedChanged={setSpeed}
              style={{
                padding: "10px 10px 10px 0px",
              }}
            />
            <div
              style={{
                padding: "8px",
                margin: "9px",
                border: "1px solid #999999",
                borderRadius: "5px",
              }}
            >
              <MotorOffsetTable
                opticFeature={opticFeature}
                motorCalibrations={motorCalibration}
                onMotorCalibrationUpdated={onMotorCalibrationUpdated}
                selectedIndex={selectedIndex}
                onSelectedIndexChanged={setSelectedIndex}
                onMoveToPosition={onMoveToPosition}
                onHome={onHome}
              />
            </div>
          </Disabled>

          <ImagerManualControl
            wellLimitEnabled={false}
            imagerIdentifier={imagerIdentifier}
            style={{
              overflow: "none",
            }}
          />
        </>
      )}

      {!showCalibration && (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "gray",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "20px",
            padding: "20px",
          }}
        >
          {"There are no discrete optic motor on " +
            LightPathUtil.toName(lightPath) +
            " lightpath please select other lightpath"}
        </div>
      )}
      <Prompt
        when={isCalibrationUpdated}
        message={(location) =>
          `Changes are not saved, are you sure you want to leave Motor Calibration ?`
        }
      />
    </CommandPanel>
  );
}
